import React, { useState, useEffect, useRef } from 'react';
import { GetRequest, PostRequest } from 'components/common';
import { ContentPopup } from 'components/ContentPopup.jsx';
import SheetPreview from './SheetPreview';

const DataSourcePriceInput = ({
  value,
  onChange,
  currency,
  precision,
  dataSourceConfig,
  onDataSourceConfigChange,
  disabled,
  userUnitPriceId,
  attributeName
}) => {
  const [dataSources, setDataSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfigPopup, setShowConfigPopup] = useState(false);
  const [selectedDataSource, setSelectedDataSource] = useState(null);
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [selectedSpreadsheet, setSelectedSpreadsheet] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(null); // { id, name }
  const [cellReference, setCellReference] = useState('');
  const [fetchedValue, setFetchedValue] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [fetchingValue, setFetchingValue] = useState(false);
  const [lastSpreadsheetId, setLastSpreadsheetId] = useState(null);
  const popupRef = useRef();

  const isLinkedToDataSource = dataSourceConfig && dataSourceConfig.data_source_id;

  useEffect(() => {
    fetchDataSources();

    // Listen for OAuth popup completion (Google Sheets and Microsoft Excel)
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data && (event.data.type === 'google_sheets_oauth_complete' || event.data.type === 'microsoft_excel_oauth_complete')) {
        fetchDataSources();
        if (event.data.result.success && event.data.result.dataSourceId) {
          const newDataSourceId = event.data.result.dataSourceId;
          setSelectedDataSource(newDataSourceId);
          // Fetch spreadsheets/workbooks for the newly linked data source
          // We'll refetch data sources first and then fetch files based on source type
          GetRequest({
            url: '/quote-builder/data-sources',
            callback: (response) => {
              setDataSources(response.data_sources);
              const newDataSource = response.data_sources.find(ds => ds.id == newDataSourceId);
              if (newDataSource) {
                fetchFilesForDataSource(newDataSourceId, newDataSource.source_type);
              }
            }
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (dataSourceConfig) {
      setSelectedDataSource(dataSourceConfig.data_source_id);
      setSelectedSpreadsheet(dataSourceConfig.spreadsheet_id);
      setSelectedSheet({
        id: dataSourceConfig.sheet_id,
        name: dataSourceConfig.sheet_name
      });
      setCellReference(dataSourceConfig.cell_reference);
      setFetchedValue(dataSourceConfig.last_fetched_value);
    }
  }, [dataSourceConfig]);

  // When popup opens with existing config, fetch spreadsheets and sheets
  useEffect(() => {
    if (showConfigPopup && dataSourceConfig && dataSourceConfig.data_source_id) {
      // Fetch spreadsheets for the selected data source
      fetchSpreadsheets(dataSourceConfig.data_source_id);
      // Also fetch sheets for the selected spreadsheet
      if (dataSourceConfig.spreadsheet_id) {
        setLoading(true);
        GetRequest({
          url: `/quote-builder/data-sources/${dataSourceConfig.data_source_id}/spreadsheet/${dataSourceConfig.spreadsheet_id}/sheets`,
          callback: (data) => {
            setSheets(data);
            // If we have a sheet_name but no sheet_id (legacy data), find the sheet by name
            if (dataSourceConfig.sheet_name && !dataSourceConfig.sheet_id) {
              const matchingSheet = data.find(s => s.name === dataSourceConfig.sheet_name);
              if (matchingSheet) {
                setSelectedSheet({ id: matchingSheet.id, name: matchingSheet.name });
              }
            }
            setLoading(false);
          }
        });
      }
    } else if (showConfigPopup && !dataSourceConfig && dataSources.length === 1 && !selectedDataSource) {
      // Auto-select if there's only one data source and nothing is selected yet
      handleDataSourceSelect(dataSources[0].id);
    }
  }, [showConfigPopup]);

  const fetchDataSources = () => {
    GetRequest({
      url: '/quote-builder/data-sources',
      callback: (response) => {
        setDataSources(response.data_sources);
        if (response.last_spreadsheet_id) {
          setLastSpreadsheetId(response.last_spreadsheet_id);
        }
      }
    });
  };

  const getSelectedDataSourceType = () => {
    if (!selectedDataSource) return null;
    const ds = dataSources.find(d => d.id == selectedDataSource);
    return ds ? ds.source_type : null;
  };

  const fetchFilesForDataSource = (dataSourceId, sourceType) => {
    if (sourceType === 'google_sheets') {
      fetchSpreadsheets(dataSourceId);
    } else if (sourceType === 'microsoft_excel') {
      fetchWorkbooks(dataSourceId);
    }
  };

  const handleConnectGoogleSheets = () => {
    openOAuthPopup('/quote-builder/data-sources/google-sheets/authorize', 'google_sheets_auth');
  };

  const handleConnectMicrosoftExcel = () => {
    openOAuthPopup('/quote-builder/data-sources/microsoft-excel/authorize', 'microsoft_excel_auth');
  };

  const openOAuthPopup = (url, name) => {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      url,
      name,
      `width=${width},height=${height},left=${left},top=${top}`
    );

    const checkPopup = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopup);
        fetchDataSources();
      }
    }, 500);
  };

  const fetchSpreadsheets = (dataSourceId, autoSelectSpreadsheetId = null) => {
    if (dataSourceId) {
      setLoading(true);
      GetRequest({
        url: `/quote-builder/data-sources/${dataSourceId}/spreadsheets`,
        callback: (data) => {
          setSpreadsheets(data);
          setLoading(false);
          // Auto-select the last used spreadsheet if it exists in the list
          const spreadsheetToSelect = autoSelectSpreadsheetId || lastSpreadsheetId;
          if (spreadsheetToSelect && !selectedSpreadsheet) {
            const matchingSpreadsheet = data.find(s => s.id === spreadsheetToSelect);
            if (matchingSpreadsheet) {
              // Pass dataSourceId explicitly since selectedDataSource may not be updated yet
              handleSpreadsheetSelect(spreadsheetToSelect, dataSourceId);
            }
          }
        }
      });
    }
  };

  const fetchWorkbooks = (dataSourceId, autoSelectWorkbookId = null) => {
    if (dataSourceId) {
      setLoading(true);
      GetRequest({
        url: `/quote-builder/data-sources/${dataSourceId}/workbooks`,
        callback: (data) => {
          setSpreadsheets(data); // Reuse spreadsheets state for workbooks
          setLoading(false);
          // Auto-select the last used workbook if it exists in the list
          const workbookToSelect = autoSelectWorkbookId || lastSpreadsheetId;
          if (workbookToSelect && !selectedSpreadsheet) {
            const matchingWorkbook = data.find(s => s.id === workbookToSelect);
            if (matchingWorkbook) {
              handleWorkbookSelect(workbookToSelect, dataSourceId);
            }
          }
        }
      });
    }
  };

  const handleDataSourceSelect = (dataSourceId) => {
    setSelectedDataSource(dataSourceId);
    setSpreadsheets([]);
    setSheets([]);
    setSelectedSpreadsheet(null);
    setSelectedSheet(null);
    setCellReference('');
    setFetchedValue(null);
    setFetchError(null);

    const ds = dataSources.find(d => d.id == dataSourceId);
    if (ds) {
      fetchFilesForDataSource(dataSourceId, ds.source_type);
    }
  };

  const handleRefreshSpreadsheets = () => {
    if (selectedDataSource) {
      setSpreadsheets([]);
      const ds = dataSources.find(d => d.id == selectedDataSource);
      if (ds) {
        fetchFilesForDataSource(selectedDataSource, ds.source_type);
      }
    }
  };

  const handleSpreadsheetSelect = (spreadsheetId, dataSourceIdOverride = null) => {
    setSelectedSpreadsheet(spreadsheetId);
    setSheets([]);
    setSelectedSheet(null);
    setFetchedValue(null);
    setFetchError(null);

    const dataSourceId = dataSourceIdOverride || selectedDataSource;
    if (spreadsheetId && dataSourceId) {
      setLoading(true);
      GetRequest({
        url: `/quote-builder/data-sources/${dataSourceId}/spreadsheet/${spreadsheetId}/sheets`,
        callback: (data) => {
          setSheets(data);
          // Auto-select the first sheet to show the preview immediately
          if (data.length > 0) {
            setSelectedSheet({ id: data[0].id, name: data[0].name });
          }
          setLoading(false);
        }
      });
    }
  };

  const handleWorkbookSelect = (workbookId, dataSourceIdOverride = null) => {
    setSelectedSpreadsheet(workbookId);
    setSheets([]);
    setSelectedSheet(null);
    setFetchedValue(null);
    setFetchError(null);

    const dataSourceId = dataSourceIdOverride || selectedDataSource;
    if (workbookId && dataSourceId) {
      setLoading(true);
      GetRequest({
        url: `/quote-builder/data-sources/${dataSourceId}/workbook/${workbookId}/worksheets`,
        callback: (data) => {
          setSheets(data);
          // Auto-select the first worksheet to show the preview immediately
          if (data.length > 0) {
            setSelectedSheet({ id: data[0].id, name: data[0].name });
          }
          setLoading(false);
        }
      });
    }
  };

  const handleFileSelect = (fileId, dataSourceIdOverride = null) => {
    const dataSourceId = dataSourceIdOverride || selectedDataSource;
    const ds = dataSources.find(d => d.id == dataSourceId);
    if (ds && ds.source_type === 'microsoft_excel') {
      handleWorkbookSelect(fileId, dataSourceId);
    } else {
      handleSpreadsheetSelect(fileId, dataSourceId);
    }
  };

  const handleSaveConfig = () => {
    if (!selectedDataSource || !selectedSpreadsheet || !selectedSheet || !cellReference) {
      return;
    }

    // Find the spreadsheet name from the list
    const selectedSpreadsheetObj = spreadsheets.find(s => s.id === selectedSpreadsheet);
    const spreadsheetName = selectedSpreadsheetObj ? selectedSpreadsheetObj.name : null;

    const config = {
      data_source_id: selectedDataSource,
      spreadsheet_id: selectedSpreadsheet,
      spreadsheet_name: spreadsheetName,
      sheet_id: selectedSheet.id,
      sheet_name: selectedSheet.name, // Keep name for display purposes
      cell_reference: cellReference,
      last_fetched_value: fetchedValue,
      last_fetched_at: new Date().toISOString()
    };

    onDataSourceConfigChange(config);

    if (fetchedValue !== null) {
      // Remove currency symbols, commas, and whitespace before parsing
      const cleanedValue = String(fetchedValue).replace(/[^0-9.-]/g, '');
      const numericValue = parseFloat(cleanedValue);
      if (!isNaN(numericValue)) {
        onChange({ target: { value: numericValue.toFixed(precision || 2) } });
      }
    }

    setShowConfigPopup(false);
  };

  const handleUnlinkDataSource = () => {
    if (!userUnitPriceId || !attributeName) {
      // Fallback to just updating local state if we don't have the IDs
      onDataSourceConfigChange(null);
      setSelectedDataSource(null);
      setSelectedSpreadsheet(null);
      setSelectedSheet(null);
      setCellReference('');
      setFetchedValue(null);
      return;
    }

    // Make API call to unlink the data source immediately
    PostRequest({
      url: `/quote-builder/user_unit_prices/${userUnitPriceId}/unlink_data_source`,
      data: { attribute_name: attributeName },
      callback: () => {
        onDataSourceConfigChange(null);
        setSelectedDataSource(null);
        setSelectedSpreadsheet(null);
        setSelectedSheet(null);
        setCellReference('');
        setFetchedValue(null);
      },
      400: (data) => {
        alert(data.errors || 'Failed to unlink data source');
      }
    });
  };

  const handleRefresh = () => {
    if (isLinkedToDataSource) {
      setFetchingValue(true);
      PostRequest({
        url: `/quote-builder/data-sources/${dataSourceConfig.data_source_id}/fetch-cell`,
        data: {
          spreadsheet_id: dataSourceConfig.spreadsheet_id,
          sheet_id: dataSourceConfig.sheet_id,
          sheet_name: dataSourceConfig.sheet_name,
          cell_reference: dataSourceConfig.cell_reference
        },
        callback: (data) => {
          setFetchingValue(false);
          if (!data.error && data.value !== null) {
            // Remove currency symbols, commas, and whitespace before parsing
            const cleanedValue = String(data.value).replace(/[^0-9.-]/g, '');
            const numericValue = parseFloat(cleanedValue);
            if (!isNaN(numericValue)) {
              onChange({ target: { value: numericValue.toFixed(precision || 2) } });
              onDataSourceConfigChange({
                ...dataSourceConfig,
                last_fetched_value: data.value,
                last_fetched_at: data.fetched_at
              });
            }
          }
        }
      });
    }
  };

  const getDataSourceName = () => {
    if (!dataSourceConfig) return '';
    const ds = dataSources.find(d => d.id === dataSourceConfig.data_source_id);
    return ds ? ds.name : '';
  };

  const configPopupContent = (
    <div className="data-source-config" style={{ textAlign: 'left' }}>
      <h3>{t('quote_builder.configure_data_source')}</h3>

      {dataSources.length === 0 ? (
        <div className="no-data-sources">
          <p>{t('quote_builder.no_data_sources_connected')}</p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-default"
              onClick={handleConnectGoogleSheets}
              style={{ padding: '6px 12px' }}
            >
              <img src="/assets/google-sheets.svg" alt="Google Sheets" style={{ height: '24px' }} />
            </button>
            <button
              type="button"
              className="btn btn-primary"
              style={{ backgroundColor: '#217346', borderColor: '#217346' }}
              onClick={handleConnectMicrosoftExcel}
            >
              <i className="fa fa-windows"></i> {t('quote_builder.connect_microsoft_excel')}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>{t('quote_builder.data_source')}</label>
            <select
              className="form-control"
              style={{ flex: '1 1 auto' }}
              value={selectedDataSource || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '__add_google_sheets__') {
                  handleConnectGoogleSheets();
                } else if (value === '__add_microsoft_excel__') {
                  handleConnectMicrosoftExcel();
                } else {
                  handleDataSourceSelect(value);
                }
              }}
            >
              <option value="">{t('quote_builder.select_data_source')}</option>
              {dataSources.map(ds => (
                <option key={ds.id} value={ds.id}>{ds.name}</option>
              ))}
              <option disabled>──────────</option>
              <option value="__add_google_sheets__">+ {t('quote_builder.add_google_sheets_source')}</option>
              <option value="__add_microsoft_excel__">+ {t('quote_builder.add_microsoft_excel_source')}</option>
            </select>
          </div>

          {selectedDataSource && (
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>
                {getSelectedDataSourceType() === 'microsoft_excel'
                  ? t('quote_builder.workbook')
                  : t('quote_builder.spreadsheet')}
              </label>
              {loading ? (
                <span><i className="fa fa-spinner fa-spin"></i> {t('quote_builder.loading')}</span>
              ) : (
                <select
                  className="form-control"
                  style={{ flex: '1 1 auto' }}
                  value={selectedSpreadsheet || ''}
                  onChange={(e) => handleFileSelect(e.target.value)}
                >
                  <option value="">
                    {getSelectedDataSourceType() === 'microsoft_excel'
                      ? t('quote_builder.select_workbook')
                      : t('quote_builder.select_spreadsheet')}
                  </option>
                  {spreadsheets.map(ss => (
                    <option key={ss.id} value={ss.id}>{ss.name}</option>
                  ))}
                </select>
              )}
              <small style={{ whiteSpace: 'nowrap' }}>
                <a href="#" onClick={(e) => { e.preventDefault(); handleRefreshSpreadsheets(); }}>
                  <i className="fa fa-refresh"></i> {t('quote_builder.refresh_file_list')}
                </a>
              </small>
            </div>
          )}

          {selectedSheet && (
            <div className="form-group">
              <SheetPreview
                dataSourceId={selectedDataSource}
                spreadsheetId={selectedSpreadsheet}
                sheetId={selectedSheet.id}
                sheetName={selectedSheet.name}
                sheets={sheets}
                onSheetSelect={(sheet) => {
                  setSelectedSheet({ id: sheet.id, name: sheet.name });
                  setCellReference('');
                  setFetchedValue(null);
                  setFetchError(null);
                }}
                selectedCell={cellReference}
                onCellSelect={(cellRef, cellValue) => {
                  setCellReference(cellRef);
                  setFetchedValue(cellValue);
                  setFetchError(null);
                }}
              />
            </div>
          )}

          {fetchError && (
            <div className="alert alert-danger">
              <i className="fa fa-exclamation-circle"></i> {fetchError}
            </div>
          )}

          {fetchedValue !== null && !fetchError && (
            <div className="alert alert-success">
              <strong>{t('quote_builder.selected_cell')}:</strong> {cellReference}, <strong>{t('quote_builder.fetched_value')}:</strong> {fetchedValue} {currency}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveConfig}
              disabled={!fetchedValue}
            >
              {t('quote_builder.save_and_use_value')}
            </button>
            <button
              type="button"
              className="btn btn-default"
              onClick={() => setShowConfigPopup(false)}
            >
              {t('quote_builder.cancel')}
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="data-source-price-input">
      {isLinkedToDataSource ? (
        <div className="linked-data-source">
          <div className="data-source-info">
            <small>
              <i className="fa fa-link"></i> {getDataSourceName()} - {dataSourceConfig.sheet_name}!{dataSourceConfig.cell_reference}
              {' '}
              <a href="#" onClick={(e) => { e.preventDefault(); handleRefresh(); }}>
                {fetchingValue ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-refresh"></i>}
              </a>
              {' | '}
              <a href="#" onClick={(e) => { e.preventDefault(); setShowConfigPopup(true); }}>
                {t('quote_builder.edit')}
              </a>
              {' | '}
              <a href="#" onClick={(e) => { e.preventDefault(); handleUnlinkDataSource(); }}>
                {t('quote_builder.unlink')}
              </a>
            </small>
          </div>
        </div>
      ) : (
        <div className="manual-entry">
          <small>
            <a href="#" onClick={(e) => { e.preventDefault(); setShowConfigPopup(true); }}>
              <i className="fa fa-link"></i> {t('quote_builder.link_to_data_source')}
            </a>
          </small>
        </div>
      )}

      {showConfigPopup && (
        <ContentPopup
          myRef={popupRef}
          autoOpen={true}
          content={configPopupContent}
          cancelButton={false}
          beforeClose={() => setShowConfigPopup(false)}
        />
      )}
    </div>
  );
};

export default DataSourcePriceInput;
